import type { DashboardView } from "../../redux/portfolio.types"
import {
  createEmptyFilterLabels,
  createEmptyFilters
} from "../../redux/globalFilters"
import type {
  CatalogWidgetSpec,
  WidgetCatalog,
  WidgetInstanceV4
} from "../../redux/widgets.types"
import { specToInstance } from "./widgetsResponseAdapter"

/**
 * The GC Default view.
 *
 * WHY THIS EXISTS AT ALL. Home used to render ONE composition whatever view
 * was selected — the saved view chose which widgets the grid held, never how
 * the page was arranged. So a "new default layout" had nowhere to live: every
 * account landed on `normalizeToV3`'s fallback, literally named "My View",
 * showing the legacy arrangement. GC Default is the first view whose identity
 * selects a COMPOSITION, not just a widget set.
 *
 * It is a real, persisted view — not a flag and not a branch on the user's
 * role — and it is the MAIN one: a one-time, versioned migration
 * (`HOME_DEFAULT_VERSION`) adds it to every account, seeds it with the full
 * default widget set and makes it active, keeping the account's previous
 * views intact and selectable. After that the stored config is the user's:
 * switching away, or removing a widget, is never undone on a later load.
 */
export const GC_DEFAULT_VIEW_ID = "gc_default"
export const GC_DEFAULT_VIEW_NAME = "My View"
/** The name GC Default was stored under before it was shown as My View. */
export const LEGACY_GC_DEFAULT_NAME = "GC Default"

/**
 * Bump when the default composition changes in a way existing accounts should
 * receive, and list what the new version adds in `DEFAULT_KEYS_ADDED_IN`.
 *
 * Version 1 is the first migration: GC Default is added, made active and
 * seeded with every default. Any later version only ADDS its own new widgets
 * to GC Default — the account's active view, its other views and every
 * widget it removed are left exactly as saved.
 */
export const HOME_DEFAULT_VERSION = 2

/** The widgets each later default version introduced. */
export const DEFAULT_KEYS_ADDED_IN: Readonly<
  Record<number, readonly string[]>
> = {
  // The financial band's third figure, which had no metric before.
  2: ["overdue_receivables"]
}

/** Catalog keys a config at `fromVersion` has not been given yet. */
export const defaultKeysAddedSince = (fromVersion: number): string[] =>
  Object.entries(DEFAULT_KEYS_ADDED_IN)
    .filter(([version]) => Number(version) > fromVersion)
    .sort(([a], [b]) => Number(a) - Number(b))
    .flatMap(([, keys]) => [...keys])

export const isGcDefaultView = (viewId: string | null | undefined): boolean =>
  viewId === GC_DEFAULT_VIEW_ID

const makeGcDefaultView = (): DashboardView => ({
  id: GC_DEFAULT_VIEW_ID,
  key: GC_DEFAULT_VIEW_ID,
  name: GC_DEFAULT_VIEW_NAME,
  order: [],
  hidden: [],
  globalFilters: createEmptyFilters(),
  filterLabels: createEmptyFilterLabels()
})

/**
 * The name a view is SHOWN under. Only the label changes: the view keeps its
 * id (`gc_default`) and everything saved against it.
 *
 * The main view reads as "My View". A config saved before that kept the old
 * stored name "GC Default", and an account migrated from the single-view era
 * still holds that era's placeholder "My View" — shown as "Previous layout",
 * so the list never offers two views with one name. A name the user typed
 * themselves is always shown as typed.
 */
export const viewDisplayName = (
  view: { id: string; name: string },
  t: (key: string, fallback: string) => string
): string => {
  if (view.id === GC_DEFAULT_VIEW_ID && view.name === LEGACY_GC_DEFAULT_NAME)
    return t("portfolio.views.myView", "My View")
  if (view.id === "default" && view.name === "My View")
    return t("portfolio.views.previousLayout", "Previous layout")
  return view.name
}

/** The Home default version a stored config was last migrated to, if any. */
export const homeDefaultVersionOf = (raw: unknown): number | undefined => {
  if (raw == null || typeof raw !== "object" || Array.isArray(raw))
    return undefined
  const version = (raw as { homeDefaultVersion?: unknown }).homeDefaultVersion
  return typeof version === "number" ? version : undefined
}

/**
 * Has this config been through the first migration onto GC Default?
 *
 * Deliberately not "is it on the CURRENT version": a later version only adds
 * widgets, and must never re-open GC Default over a view the user chose.
 */
export const hasHomeDefault = (raw: unknown): boolean =>
  (homeDefaultVersionOf(raw) ?? 0) >= 1

/**
 * Did this account ever save a dashboard layout of its own?
 *
 * A fresh account becomes GC Default outright (its "My View" is only a
 * placeholder for an absent config). A saved account keeps every view it has
 * and gains GC Default beside them — that is what "preserve the previous
 * layout as a recoverable saved view" means in practice.
 */
export const hasStoredLayout = (raw: unknown): boolean => {
  if (raw == null || typeof raw !== "object" || Array.isArray(raw)) return false
  const obj = raw as Record<string, unknown>
  if (Array.isArray(obj.views) && obj.views.length > 0) return true
  if (Array.isArray(obj.tiles) && obj.tiles.length > 0) return true
  if (Array.isArray(obj.order) && obj.order.length > 0) return true
  return false
}

/**
 * The widgets GC Default opens with, as CATALOG KEYS in section order.
 *
 * Keys rather than slugs, because the slug is derived from the catalog spec
 * (`specToInstance`) and the catalog is already filtered to what this user
 * may read — a key the catalog does not offer is simply not seeded, which is
 * how permissions are respected without a second rule here. Cash Flow and
 * Project Status are the always-on fixed instances and are added separately.
 */
export const DEFAULT_WIDGET_KEYS: readonly string[] = [
  // At a glance — the band reads these KPIs; Next Best Actions feeds the cards.
  "active_projects",
  "delayed_projects",
  "pending_approvals",
  "nba",
  // Financial — the band's money figures, the snapshot beside Cash Flow.
  "revenue_in_window",
  "outstanding_receivables",
  "portfolio_financial_snapshot",
  // Schedule
  "upcoming_milestones",
  // Pipeline — every active lead at its lifecycle stage. Not `sales_pipeline`,
  // which counts only leads holding an open proposal, so a New or Contacted
  // lead (no proposal yet) would be missing from the funnel.
  "lead_stages",
  "win_rate",
  "activity_feed",
  // Version 2: the financial band's Overdue receivables.
  "overdue_receivables"
]

/**
 * Config a default widget is seeded with on top of the catalog's own.
 *
 * `upcoming_milestones` resolves 10 rows by default, soonest first, and the
 * At-a-glance "Milestones this week" count is taken from those rows — so an
 * account with more than ten due in the next seven days would be undercounted.
 * 50 is the backend's `MAX_ROW_LIMIT`; the card itself still shows three.
 */
const SEED_CONFIG: Record<string, Record<string, unknown>> = {
  upcoming_milestones: { limit: 50 }
}

/**
 * The default widget instances this user can actually have.
 *
 * Resolved against the loaded catalog so the result is permission-filtered
 * and carries the exact slugs the rest of the dashboard keys on. Order is the
 * section order above, which is also the reading order the page renders.
 */
export const defaultInstancesFrom = (
  catalog: WidgetCatalog | null,
  fixed: WidgetInstanceV4[],
  keys: readonly string[] = DEFAULT_WIDGET_KEYS
): WidgetInstanceV4[] => {
  const byKey = new Map<string, CatalogWidgetSpec>(
    (catalog?.widgets ?? []).map((spec) => [spec.key, spec])
  )
  const seeded: WidgetInstanceV4[] = []
  const seen = new Set<string>()
  for (const key of keys) {
    const spec = byKey.get(key)
    if (!spec) continue
    const base = specToInstance(spec)
    const instance = SEED_CONFIG[key]
      ? { ...base, config: { ...base.config, ...SEED_CONFIG[key] } }
      : base
    if (seen.has(instance.slug)) continue
    seen.add(instance.slug)
    seeded.push(instance)
  }
  for (const instance of fixed) {
    if (!seen.has(instance.slug)) {
      seen.add(instance.slug)
      seeded.push(instance)
    }
  }
  return seeded
}

/**
 * Puts GC Default in the view list and decides which view opens.
 *
 * `migrated` is whether the stored config already carries the current
 * `homeDefaultVersion`. Until it does, GC Default is added (never replacing a
 * personal view) and made active — the one-time migration. Once it does, the
 * stored active view is the user's own later choice and is respected.
 */
export const withGcDefaultView = (
  views: DashboardView[],
  activeViewId: string | null,
  storedLayout: boolean,
  migrated: boolean
): { views: DashboardView[]; activeViewId: string | null } => {
  if (!storedLayout) {
    return { views: [makeGcDefaultView()], activeViewId: GC_DEFAULT_VIEW_ID }
  }
  const present = views.some((view) => view.id === GC_DEFAULT_VIEW_ID)
  const next = present ? views : [...views, makeGcDefaultView()]
  if (!migrated) {
    return { views: next, activeViewId: GC_DEFAULT_VIEW_ID }
  }
  return { views: next, activeViewId: activeViewId ?? next[0]?.id ?? null }
}
